import { inject, Injectable } from '@angular/core';
import { Observable, map, switchMap, of, catchError } from 'rxjs';
import type { Task } from '@web-portal/domains/shared/domain-models';
import { Api } from 'bimplus-websdk';
import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import { buildAuthenticatedUrl } from 'utilities';
import { TranslateService } from '@ngx-translate/core';
import {
  TasksAdapterService,
  Member,
  TaskUser,
  CreateTaskPayload,
  ScenesAdapterService,
  AttachmentsAdapterService,
  ProjectsAdapterService,
} from '../../providers';
import type { SceneSetupPayload } from '../host-communication/messages/scene-setup/interfaces/scene-setup-payload.interface';
import { createThumbnailFormData } from '../../utils/blob-utils';
import {
  showInvalidThumbnailToast,
  showInvalidThumbnailUrlToast,
  showSceneCreationFailedToast,
} from '../../utils/scene-thumbnail-toast-utils';
import { UserStrategy } from '../../strategies';

@Injectable({ providedIn: 'root' })
export class TasksService {
  private readonly api: Api = inject(BIMPLUS_API);
  private readonly adapter = inject(ProjectsAdapterService);
  private readonly tasksAdapter = inject(TasksAdapterService);
  private readonly scenesAdapter = inject(ScenesAdapterService);
  private readonly attachmentsAdapter = inject(AttachmentsAdapterService);
  private readonly userFacade = inject(UserStrategy);
  private readonly translate = inject(TranslateService);
  createTask<
    T extends {
      assignees: { id: string; name: string; email: string }[];
      watchers?: { id: string; name: string; email: string }[];
      title: string;
      description: string;
      classification: string;
      priority: string | null;
      status: string;
      dueDate: Date | null;
      scene?: SceneSetupPayload;
    },
  >(projectId: string, formResult: T, members: Member[]): Observable<string | undefined> {
    const loggedInEmail = this.userFacade.user()?.email;

    const loggedInMember = members.find(m => m.email === loggedInEmail);
    const author: TaskUser | undefined = loggedInMember
      ? {
          id: loggedInMember.id,
          email: loggedInMember.email,
          displayName: loggedInMember.name ?? '',
          firstName: loggedInMember.name?.split(' ')[0] ?? '',
          lastName: loggedInMember.name?.split(' ').slice(1).join(' ') ?? '',
        }
      : undefined;

    const assignees =
      formResult.assignees && formResult.assignees.length > 0 ? formResult.assignees : [];

    let responsibles: TaskUser[] = [];
    let responsibleEmails: string[] = [];
    if (assignees.length > 0) {
      responsibles = assignees.map(a => ({
        id: a.id,
        email: a.email,
        displayName: a.name ?? '',
        firstName: a.name?.split(' ')[0] ?? '',
        lastName: a.name?.split(' ').slice(1).join(' ') ?? '',
      }));
      responsibleEmails = assignees.map(a => a.email).filter(Boolean);
    } else if (author) {
      responsibles = [author];
      responsibleEmails =
        typeof author.email === 'string' && author.email.length > 0 ? [author.email] : [];
    }

    // Build viewers payload — the BIM+ POST endpoint ignores viewers,
    // so a follow-up PUT is issued after task creation.
    // The PUT only needs { id, email } per viewer (same as task-detail updateField).
    const viewersPayload = formResult.watchers?.length
      ? formResult.watchers.map(w => ({ id: w.id, email: w.email }))
      : undefined;

    const payload: CreateTaskPayload = {
      name: formResult.title,
      description: formResult.description,
      classification: formResult.classification,
      priority: formResult.priority,
      status: formResult.status,
      published: true,
      dueDate: formResult.dueDate?.toISOString(),
      author: author as unknown as Record<string, unknown>,
      responsibles: responsibles as unknown as Record<string, unknown>[],
      responsibleEmails,
    };

    return this.adapter.createTask(projectId, payload).pipe(
      switchMap(createdTask => {
        const scene = formResult.scene;
        const taskId = createdTask.id;

        // The BIM+ POST endpoint does not persist viewers — follow up with a PUT.
        // Use the POST response as the base to avoid a GET, but override responsible/
        // responsibles in the patch with the clean { id, email } format from our
        // original mapping. The raw DtoTask fields (lowercase firstname, etc.) in the
        // casted base object are ignored by the API when the patch overrides them.
        const setViewers$ =
          viewersPayload?.length && taskId
            ? this.tasksAdapter
                .updateTask(taskId, createdTask as unknown as Task, {
                  viewers: viewersPayload,
                  responsible: null,
                  responsibles: responsibles.map(r => ({ id: r.id, email: r.email })),
                  responsibleEmails: responsibles
                    .map(r => r.email)
                    .filter(Boolean)
                    .join(','),
                })
                .pipe(catchError(() => of(undefined)))
            : of(undefined);

        const createScene$ = scene && taskId ? this.createSceneForTask(taskId, scene) : of(void 0);

        return setViewers$.pipe(switchMap(() => createScene$.pipe(map(() => taskId))));
      })
    );
  }

  /**
   * Creates a scene for a task and uploads the thumbnail.
   */
  private createSceneForTask(taskId: string, scenePayload: SceneSetupPayload): Observable<void> {
    // Extract fields that need special handling or are set by API
    const {
      thumbnail,
      id: _id, // eslint-disable-line @typescript-eslint/no-unused-vars
      parentId: _parentId, // eslint-disable-line @typescript-eslint/no-unused-vars
      orderNumber: _orderNumber, // eslint-disable-line @typescript-eslint/no-unused-vars
      objectId: _objectId, // eslint-disable-line @typescript-eslint/no-unused-vars
      attachments,
      ...cleanPayload
    } = scenePayload as SceneSetupPayload & {
      attachments?: { id?: string; url?: string; attachmenttype?: string }[];
    };

    // Map attachments with correct casing (API expects attachmentType, host sends attachmenttype)
    const mappedAttachments =
      attachments?.map(a => ({
        id: a.id,
        url: a.url,
        attachmentType: a.attachmenttype,
      })) ?? [];

    // Map camera.aspectratio to AspectRatio (host sends lowercase, API expects PascalCase)
    const mappedCamera = cleanPayload.camera
      ? {
          ...cleanPayload.camera,
          AspectRatio: (cleanPayload.camera as { aspectratio?: number | null }).aspectratio,
        }
      : undefined;
    if (mappedCamera && 'aspectratio' in mappedCamera) {
      delete (mappedCamera as Record<string, unknown>)['aspectratio'];
    }

    // Build scene DTO for creation
    const sceneDto = {
      ...cleanPayload,
      ...(mappedCamera && { camera: mappedCamera }),
      attachments: mappedAttachments,
      objectId: taskId,
      parentId: taskId,
    };

    return this.scenesAdapter.createScene(taskId, sceneDto).pipe(
      switchMap(createdScene => {
        // Upload thumbnail if provided
        const sceneId = createdScene.id;
        if (!sceneId) {
          showSceneCreationFailedToast(this.translate);
          return of(void 0);
        }

        if (!thumbnail) {
          return of(void 0);
        }

        // Chain thumbnail upload into the scene creation flow so URL thumbnails
        // (which call createThumbnailAttachment inside switchMap) are executed.
        return this.uploadSceneThumbnail(sceneId, thumbnail);
      }),
      catchError(() => {
        showSceneCreationFailedToast(this.translate);
        // Don't fail task creation if scene creation fails
        return of(void 0);
      })
    );
  }

  /**
   * Uploads thumbnail to a scene (supports base64 or URL).
   */
  private uploadSceneThumbnail(sceneId: string, thumbnail: string): Observable<void> {
    const isBase64 = thumbnail.startsWith('data:');
    if (isBase64) {
      // Base64 thumbnail - convert to FormData and upload
      const formData = createThumbnailFormData(thumbnail);
      if (!formData) {
        showInvalidThumbnailToast(this.translate);
        return of(void 0);
      }
      return this.scenesAdapter.createThumbnailAttachment(sceneId, formData).pipe(
        map(uploadResult => {
          if (uploadResult === null) {
            showInvalidThumbnailToast(this.translate);
          }
          return void 0;
        })
      );
    } else if (thumbnail.includes('/attachments/')) {
      // URL thumbnail - download and re-upload
      // Construct full URL if it's a relative path
      let downloadUrl = thumbnail;
      if (!thumbnail.startsWith('http')) {
        const apiUrl = this.api.getApiUrl();
        downloadUrl = buildAuthenticatedUrl(apiUrl, thumbnail, null);
      }

      return this.attachmentsAdapter.downloadAttachmentFromUrl(downloadUrl).pipe(
        switchMap(blob => {
          const formData = new FormData();
          formData.append('data', blob, 'thumbnail.png');
          return this.scenesAdapter.createThumbnailAttachment(sceneId, formData).pipe(
            map(uploadResult => {
              if (uploadResult === null) {
                showInvalidThumbnailUrlToast(this.translate);
              }
              return void 0;
            })
          );
        }),
        catchError(error => {
          showInvalidThumbnailUrlToast(this.translate);
          console.error('[TasksService] Failed to upload URL thumbnail:', error);
          return of(void 0);
        })
      );
    }

    return of(void 0);
  }
}
